import { useMemo } from 'react'
import useSWR from 'swr'
import { publicClient } from 'utils/wagmi'

/**
 * The gas price every transaction of the app is sent with: what the chain says NOW (eth_gasPrice) plus 10 %, never below the chain's own minimum, never a
 * number written in the code. (BSC's minimum is 0.05 gwei since 2026; the old 3 / 4 / 5 gwei pins of the PancakeSwap fork were 60 to 100 times that.)
 */
export const MIN_GAS_PRICE: Record<number, bigint> = { 56: 50_000_000n, 97: 10_000_000_000n }
export const GAS_MARGIN_PERCENT = 10n

export const withMargin = (live: bigint, chainId = 56, multiplierPercent = 100n): bigint => {
  const base = (live * (100n + GAS_MARGIN_PERCENT) * multiplierPercent) / 10_000n
  const floor = MIN_GAS_PRICE[chainId] ?? 0n
  return base > floor ? base : floor
}

/** eth_gasPrice of the chain through the app's own public client (not the wallet's guess). */
export async function getLiveGasPrice(chainId = 56, multiplierPercent = 100n): Promise<bigint> {
  const live = await publicClient({ chainId }).getGasPrice()
  return withMargin(live, chainId, multiplierPercent)
}

/** `{ gasPrice }` for a write call; empty when the node cannot answer (the wallet then decides, never a pinned value). */
export async function liveGasParams(chainId = 56): Promise<{ gasPrice?: bigint }> {
  try {
    return { gasPrice: await getLiveGasPrice(chainId) }
  } catch {
    return {}
  }
}

/** Live gas price (with margin and floor) as a hook, refreshed every 15 s. */
export function useLiveGasPrice(chainId = 56, multiplierPercent = 100n): bigint | undefined {
  const { data } = useSWR(['live-gas-price', chainId], async () => publicClient({ chainId }).getGasPrice(), { refreshInterval: 15_000, revalidateOnFocus: false, dedupingInterval: 5_000 })
  return useMemo(() => (data === undefined ? undefined : withMargin(data, chainId, multiplierPercent)), [data, chainId, multiplierPercent])
}
